import { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import Swal from "sweetalert2";
import { API_BASE } from "config/api";

export const EXPENSE_CATEGORIES = [
  { value: "utilities", label: "Utilities" },
  { value: "salaries", label: "Salaries" },
  { value: "maintenance", label: "Maintenance" },
  { value: "mission", label: "Mission" },
  { value: "welfare_payout", label: "Welfare Payout" },
  { value: "other", label: "Other" },
];

const categoryLabel = (value) => EXPENSE_CATEGORIES.find((c) => c.value === value)?.label || value;

const rowPropTypes = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serial: PropTypes.number,
  title: PropTypes.string,
  category: PropTypes.string,
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  expenseDate: PropTypes.string,
  payee: PropTypes.string,
  originalData: PropTypes.object,
});

const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = { value: PropTypes.any.isRequired };

const TitleCell = ({ value, row }) => (
  <Box textAlign="left">
    <Typography variant="button" fontWeight={600} display="block">
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {row.original.payee ? `Paid to: ${row.original.payee}` : "No payee set"}
    </Typography>
  </Box>
);
TitleCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({ original: rowPropTypes }).isRequired,
};

const CategoryCell = ({ value }) => <Chip label={categoryLabel(value)} size="small" />;
CategoryCell.propTypes = { value: PropTypes.string.isRequired };

const AmountCell = ({ value }) => (
  <Typography variant="button" fontWeight={600}>
    GHS {Number(value || 0).toFixed(2)}
  </Typography>
);
AmountCell.propTypes = { value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) };

const DateCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary">
    {value ? new Date(value).toLocaleDateString() : "-"}
  </Typography>
);
DateCell.propTypes = { value: PropTypes.string };

const ActionsCell = ({ row, handleEdit, handleDelete }) => (
  <Stack direction="row" spacing={1} justifyContent="center">
    <Button
      size="small"
      variant="outlined"
      color="primary"
      startIcon={<EditIcon fontSize="small" />}
      onClick={() => handleEdit(row.original)}
    >
      Edit
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon fontSize="small" />}
      onClick={() => handleDelete(row.original)}
    >
      Delete
    </Button>
  </Stack>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({ original: rowPropTypes }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

export default function useExpenseData() {
  const { user, church } = useAuth();

  const [expenseData, setExpenseData] = useState({
    columns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
      { Header: "Title", accessor: "title", align: "left", width: "25%", Cell: TitleCell },
      {
        Header: "Category",
        accessor: "category",
        align: "center",
        width: "15%",
        Cell: CategoryCell,
      },
      { Header: "Amount", accessor: "amount", align: "right", width: "15%", Cell: AmountCell },
      {
        Header: "Date",
        accessor: "expenseDate",
        align: "center",
        width: "15%",
        Cell: DateCell,
      },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "25%",
        Cell: (props) => (
          <ActionsCell {...props} handleEdit={handleEdit} handleDelete={handleDelete} />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);

  const handleEdit = (expense) => {
    setSelectedExpense(expense);
    setEditModalOpen(true);
  };

  const handleDelete = (expense) => {
    setSelectedExpense(expense);
    setDeleteModalOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const url = selectedExpense.id
        ? `${API_BASE}/expenses/${selectedExpense.id}`
        : `${API_BASE}/expenses`;
      const method = selectedExpense.id ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: selectedExpense.title,
          category: selectedExpense.category,
          amount: selectedExpense.amount,
          expense_date: selectedExpense.expenseDate,
          payee: selectedExpense.payee,
          notes: selectedExpense.notes,
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: "Expense successfully saved",
        icon: "success",
        confirmButtonText: "OK",
      });

      fetchExpenses();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Expense operation failed:", error);
      setEditModalOpen(false);
      Swal.fire({ title: "Error!", text: error.message, icon: "error", confirmButtonText: "OK" });
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/expenses/${selectedExpense.id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      Swal.fire({
        title: "Success!",
        text: `Expense, ${selectedExpense.title} deleted successfully`,
        icon: "success",
        confirmButtonText: "OK",
      });

      fetchExpenses();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire({ title: "Error!", text: error.message, icon: "error", confirmButtonText: "OK" });
      setExpenseData((prev) => ({ ...prev, error: error.message }));
    }
  };

  const handleAddExpense = () => {
    setSelectedExpense({
      id: null,
      title: "",
      category: "other",
      amount: "",
      expenseDate: new Date().toISOString().split("T")[0],
      payee: "",
      notes: "",
    });
    setEditModalOpen(true);
  };

  const handlePrint = () => window.print();

  const fetchExpenses = async () => {
    try {
      setExpenseData((prev) => ({ ...prev, isLoading: true }));
      if (!user || !church) return;

      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/expenses/church/${church.id}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      const expenses = result.data || result;

      if (!Array.isArray(expenses)) throw new Error("API response is not an array");

      setExpenseData((prev) => ({
        ...prev,
        rows: expenses.map((expense, index) => ({
          id: expense.id,
          serial: index + 1,
          title: expense.title,
          category: expense.category,
          amount: expense.amount,
          expenseDate: expense.expense_date,
          payee: expense.payee,
          notes: expense.notes,
          originalData: expense,
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Fetch failed:", error);
      setExpenseData((prev) => ({ ...prev, isLoading: false, error: error.message }));
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, [user, church]);

  return {
    ...expenseData,
    handleEdit,
    handleDelete,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedExpense,
    setSelectedExpense,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddExpense,
  };
}
